import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  LinearProgress,
  Typography,
  IconButton,
  Alert,
} from '@mui/material';
import { CloudUpload, Delete, Image as ImageIcon } from '@mui/icons-material';
import { uploadToCloudinary } from '../utils/cloudinaryUpload';

/**
 * Cloudinary Image Upload Component
 * Handles image selection, preview, upload progress, and URL management
 */
const CloudinaryImageUpload = ({
  label = 'Upload Image',
  onUploadComplete,
  existingImageUrl = null,
  required = false,
}) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(existingImageUrl);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadedUrl, setUploadedUrl] = useState(existingImageUrl);
  const [error, setError] = useState(null);

  // Sync with existingImageUrl prop changes
  useEffect(() => {
    if (existingImageUrl && existingImageUrl !== uploadedUrl) {
      setUploadedUrl(existingImageUrl);
      setPreviewUrl(existingImageUrl);
    }
  }, [existingImageUrl, uploadedUrl]);

  // Cleanup preview URL on unmount
  useEffect(() => {
    return () => {
      if (previewUrl && previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('Image size must be less than 10MB');
      return;
    }

    setError(null);
    
    // Revoke previous preview URL to prevent memory leak
    if (previewUrl && previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(previewUrl);
    }
    
    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setUploading(true);
    setError(null);
    setUploadProgress(0);

    try {
      const url = await uploadToCloudinary(selectedFile, (progress) => {
        setUploadProgress(progress);
      });

      setUploadedUrl(url);
      if (onUploadComplete) {
        onUploadComplete(url);
      }
      setSelectedFile(null);
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.message || 'Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setUploadedUrl(null);
    setUploadProgress(0);
    setError(null);
    if (onUploadComplete) {
      onUploadComplete(null);
    }
  };

  return (
    <FormControl fullWidth sx={{ mt: 2 }}>
      <InputLabel
        shrink
        sx={{
          position: 'relative',
          transform: 'none',
          marginBottom: '8px',
          color: 'rgba(0, 0, 0, 0.6)',
          fontSize: '0.875rem',
        }}
      >
        {label} {required && <span style={{ color: 'red' }}>*</span>}
      </InputLabel>

      <Box sx={{ mt: 4 }}>
        {/* File Selection */}
        {!uploadedUrl && !selectedFile && (
          <Box>
            <input
              accept="image/*"
              style={{ display: 'none' }}
              id="cloudinary-image-upload"
              type="file"
              onChange={handleFileSelect}
            />
            <label htmlFor="cloudinary-image-upload">
              <Button
                variant="outlined"
                component="span"
                fullWidth
                startIcon={<CloudUpload />}
                sx={{ py: 1.5 }}
              >
                Select Image
              </Button>
            </label>
          </Box>
        )}

        {/* Preview and Upload */}
        {selectedFile && !uploadedUrl && (
          <Box>
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                maxWidth: '400px',
                mb: 2,
                borderRadius: 2,
                overflow: 'hidden',
                border: '1px solid #e0e0e0',
              }}
            >
              <img
                src={previewUrl}
                alt="Preview"
                style={{ width: '100%', height: 'auto', display: 'block' }}
              />
              <IconButton
                onClick={handleRemove}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  bgcolor: 'rgba(255, 255, 255, 0.9)',
                  '&:hover': { bgcolor: 'rgba(255, 255, 255, 1)' },
                }}
                size="small"
              >
                <Delete />
              </IconButton>
            </Box>

            {uploading && (
              <Box sx={{ mb: 2 }}>
                <LinearProgress variant="determinate" value={uploadProgress} />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                  Uploading... {uploadProgress}%
                </Typography>
              </Box>
            )}

            <Button
              variant="contained"
              onClick={handleUpload}
              disabled={uploading}
              startIcon={<CloudUpload />}
              fullWidth
            >
              {uploading ? 'Uploading...' : 'Upload to Cloudinary'}
            </Button>
          </Box>
        )}

        {/* Uploaded Image Display */}
        {uploadedUrl && (
          <Box>
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                maxWidth: '400px',
                mb: 2,
                borderRadius: 2,
                overflow: 'hidden',
                border: '2px solid #4caf50',
              }}
            >
              <img
                src={uploadedUrl}
                alt="Uploaded"
                style={{ width: '100%', height: 'auto', display: 'block' }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  top: 8,
                  left: 8,
                  bgcolor: 'rgba(76, 175, 80, 0.9)',
                  color: 'white',
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  fontSize: '0.75rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                <ImageIcon fontSize="small" />
                Uploaded
              </Box>
              <IconButton
                onClick={handleRemove}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  bgcolor: 'rgba(255, 255, 255, 0.9)',
                  '&:hover': { bgcolor: 'rgba(255, 255, 255, 1)' },
                }}
                size="small"
              >
                <Delete />
              </IconButton>
            </Box>

            <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
              URL: {uploadedUrl}
            </Typography>
          </Box>
        )}

        {/* Error Display */}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
      </Box>
    </FormControl>
  );
};

export default CloudinaryImageUpload;
